/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { Button } from "@nordcloud/gnui";
import { environmentTypesOptions } from "~/components/AssignmentRules/InlineDropdownAREnvTypes";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { EnvironmentCreateInput, PersonBasicData } from "~/models";
import { ACTIONS, Can } from "~/routing";
import { personToDropdown } from "~/tools";

type Props = {
  addEnvironment: (newEnvironment: EnvironmentCreateInput) => Promise<void>;
  employees: PersonBasicData[];
};

function EnvironmentAdd({ addEnvironment, employees }: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const methods = useForm();

  const watcher = methods.watch();

  const onSubmit = async (values: EnvironmentCreateInput) => {
    setIsLoading(true);
    try {
      await addEnvironment(values);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Can action={ACTIONS.ADD_ENVIRONMENT}>
        <Button
          size="md"
          css={{ marginLeft: "auto", marginBottom: "1rem" }}
          icon="plus"
          onClick={() => setIsSidebarOpen(true)}
        >
          Add environment
        </Button>
      </Can>

      <SidebarForm
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        title="Add environment"
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarTextField name="name" label="Environment name" required />
        <SidebarDropdown
          name="type"
          options={environmentTypesOptions}
          label="Environment type"
          required
        />
        <SidebarDropdown
          name="contactPersonId"
          options={employees.map(personToDropdown)}
          label="Contact person"
          tooltip="If a person is not available in the dropdown, make sure they are added to the application's Nordcloud Team"
          required
        />
        <SidebarTextField
          name="description"
          label="Description"
          textArea
          required
        />

        <SidebarFormButtons
          submitText="Add"
          submitEnabled={!!(watcher.name && watcher.type)}
          submitLoading={isLoading}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}

export { EnvironmentAdd };
